// src/Gallery.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { galleryData } from "./Galleryjson";

const Gallery = () => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [currentSection, setCurrentSection] = useState(null);

  const openModal = (sectionIndex, imageIndex) => {
    setCurrentSection(sectionIndex);
    setSelectedIndex(imageIndex);
  };

  const closeModal = () => {
    setSelectedIndex(null);
    setCurrentSection(null);
  };

  const showNext = () => {
    if (currentSection !== null && selectedIndex !== null) {
      const images = galleryData[currentSection].images;
      setSelectedIndex((prev) => (prev + 1) % images.length);
    }
  };

  const showPrev = () => {
    if (currentSection !== null && selectedIndex !== null) {
      const images = galleryData[currentSection].images;
      setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (selectedIndex === null) return;

      if (e.key === "ArrowRight") {
        const images = galleryData[currentSection].images;
        setSelectedIndex((prev) => (prev + 1) % images.length);
      }
      if (e.key === "ArrowLeft") {
        const images = galleryData[currentSection].images;
        setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);
      }
      if (e.key === "Escape") closeModal();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selectedIndex, currentSection]);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-900 via-indigo-950 to-black py-12 overflow-hidden">
      {/* Decorative glowing blobs */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-purple-600/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-indigo-600/30 rounded-full blur-3xl animate-ping"></div>

      <div className="container mx-auto relative z-10">
        {/* Heading */}
        <h1 className="text-5xl font-extrabold text-center mb-12 text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-500 drop-shadow-lg tracking-wide">
          Gallery
        </h1>

        {/* Button to homepage */}
        <div className="text-center mb-10">
          <Link
            to="/"
            className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-500 hover:to-purple-500 transition-all shadow-xl"
          >
            ⬅ Back to Homepage
          </Link>
        </div>

        {/* Gallery Sections */}
        {galleryData.map((section, sectionIndex) => (
          <div key={section.id} className="mb-20">
            <h2 className="text-3xl font-semibold mb-8 text-center text-white tracking-wide">
              {section.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {section.images.map((image, index) => (
                <div
                  key={index}
                  className="rounded-xl overflow-hidden shadow-lg transform hover:scale-105 transition duration-500 cursor-pointer group bg-white/10 backdrop-blur-md border border-white/20"
                  onClick={() => openModal(sectionIndex, index)}
                >
                  <img
                    src={image}
                    alt={section.title}
                    className="w-full h-60 object-contain bg-black group-hover:opacity-90 transition"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Modal / Lightbox */}
      {selectedIndex !== null && currentSection !== null && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 animate-fadeIn"
          onClick={closeModal}
        >
          <div
            className="relative max-w-5xl w-full p-4 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full hover:bg-red-700 transition shadow-md"
              onClick={closeModal}
            >
              ✕
            </button>

            {/* Prev Button */}
            <button
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/70 text-gray-800 px-3 py-2 rounded-full hover:bg-white shadow-lg"
              onClick={showPrev}
            >
              ‹
            </button>

            {/* Next Button */}
            <button
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/70 text-gray-800 px-3 py-2 rounded-full hover:bg-white shadow-lg"
              onClick={showNext}
            >
              ›
            </button>

            {/* Image */}
            <img
              src={galleryData[currentSection].images[selectedIndex]}
              alt="Enlarged view"
              className="max-h-[80vh] max-w-full object-contain mx-auto rounded-lg shadow-2xl animate-zoomIn"
            />

            {/* Caption */}
            <p className="text-center text-gray-200 mt-4 text-lg italic">
              {galleryData[currentSection].title} – Image {selectedIndex + 1}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
